import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { setSeo, setJsonLd, SITE_URL } from "@/lib/seo";

const OG = "https://images.unsplash.com/photo-1531819177115-428566ccfb50?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const META = {
  "/": { title: "UNICORP | Global Corporate Services — Incorporation, Banking & Compliance", description: "Premium global company formation, business banking and compliance across leading jurisdictions. Transparent pricing, remote onboarding, managed end to end." },
  "/banks": { title: "Business Bank Accounts | Digital, EMI & Traditional | UNICORP", description: "Open corporate and multi-currency business accounts with digital, EMI and traditional providers — application prepared and managed for you." },
  "/more-services": { title: "More Services | Corporate, Wealth, Licensing & US | UNICORP", description: "Ready-made companies, nominees, re-domiciliation, foundations, DAO structuring, EIN & ITIN, citizenship and more — beyond incorporation." },
  "/digital-studio": { title: "Digital Studio | Websites, Apps & Custom Software | UNICORP", description: "From idea to digital product — websites, Android & iOS apps, web applications, SaaS, AI and custom software with transparent estimates." },
  "/contact": { title: "Contact UNICORP | Talk to a Corporate Services Specialist", description: "Get in touch with UNICORP for company formation, banking and compliance — email, phone, WhatsApp and live chat support." },
  "/partnership": { title: "UNICORP Partner Program | Earn Up to 12% on Eligible Referrals", description: "Refer clients for company formation, banking assistance and corporate services. Explore UNICORP partner tiers and earn up to 12% on eligible referrals." },
  "/about": { title: "Who We Are | UNICORP Corporate Services", description: "UNICORP helps businesses form, bank, structure, operate and grow across global jurisdictions, with a dedicated specialist per case." },
  "/legal/terms": { title: "Terms of Service | UNICORP", description: "The terms governing your use of UNICORP's website and corporate services." },
  "/legal/privacy": { title: "Privacy Policy | UNICORP", description: "How UNICORP collects, uses and protects your personal information, and your rights over your data." },
  "/legal/refund": { title: "Refund & Cancellation Policy | UNICORP", description: "When cancellations and refunds may be available for UNICORP corporate services." },
};
const NOINDEX = ["/dashboard", "/login", "/admin", "/requests", "/start", "/payment"];
// Pages that set their own SEO in-component (leave them alone):
const SELF_MANAGED = ["/service/", "/company-formation", "/country/", "/bank/", "/compare", "/bank-compare", "/payment-gateway"];

// Segment-aware: "/payment" matches "/payment/success" but NOT "/payment-gateway".
const hasPrefix = (pathname, p) => pathname === p || pathname === p + "/" || pathname.startsWith(p + "/");

// Global scroll + SEO manager: reset scroll to top on route change (honouring
// #hash anchors) and apply per-route title / description / canonical / robots.
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash && !hash.includes("session_id=")) {
      const jump = () => {
        const el = document.getElementById(hash.slice(1));
        if (el) { el.scrollIntoView({ behavior: "smooth" }); return true; }
        return false;
      };
      if (!jump()) setTimeout(jump, 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  useEffect(() => {
    // Site-wide Organization + WebSite structured data (single configurable base URL).
    setJsonLd("org-website-schema", {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "Organization", name: "UNICORP Services Limited", url: SITE_URL + "/", logo: OG, email: "info@uniincorp.com", telephone: "+852 9854 8459",
          address: { "@type": "PostalAddress", streetAddress: "Suite C, Level 7, World Trust Tower, 50 Stanley Street", addressLocality: "Central", addressCountry: "HK" },
          sameAs: ["https://www.linkedin.com/company/unicorpltd", "https://www.facebook.com/unicorpltd/"] },
        { "@type": "WebSite", name: "UNICORP", url: SITE_URL + "/" },
      ],
    });

    const url = SITE_URL + pathname;
    if (NOINDEX.some((p) => hasPrefix(pathname, p))) {
      setSeo({ title: "UNICORP", description: "", url, noindex: true });
    } else if (SELF_MANAGED.some((p) => pathname.startsWith(p))) {
      // handled by the page component
    } else if (META[pathname]) {
      setSeo({ ...META[pathname], url, image: OG });
    } else {
      // Unknown path → the NotFound route renders; keep it out of the index.
      setSeo({ title: "Page not found (404) | UNICORP", description: "", url, noindex: true });
    }
  }, [pathname]);

  return null;
}
